import React, { useState, useEffect, useMemo, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import useSwipe from '../hooks/useSwipe';
import { Banner } from './ui';

function isLumpSum(taskCode) {
  return (taskCode || '').toLowerCase().includes('lump');
}

const EntryCard = forwardRef(function EntryCard({
  favorite,
  entry,
  rateCodes,
  defaultRateCodeId,
  isSubmitted,
  holidayNA,       // holiday job on a non-holiday day: hours can't be entered
  maxHours = 24,   // 8 on a holiday job
  onSave,
  onDelete,
  onSwipeLeft,
  onSwipeRight,
  onPrevFav,
  onNextFav,
  onSaveNextDay,
  showSaveNextDay,
  favIndex,
  favCount,
  favorites,
}, ref) {
  const [hours, setHours] = useState('');
  const [rateCodeId, setRateCodeId] = useState('');
  const [description, setDescription] = useState('');
  const [nonBillable, setNonBillable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [dirty, setDirty] = useState(false);
  const savingRef = useRef(false);

  const lumpSum = isLumpSum(favorite?.TaskCode);

  const filteredRateCodes = useMemo(() => {
    if (!favorite?.RateSheetCode) return rateCodes;
    const filtered = rateCodes.filter((rc) => rc.RateSheetCode === favorite.RateSheetCode);
    return filtered.length > 0 ? filtered : rateCodes;
  }, [rateCodes, favorite]);

  // Sync form with current entry or defaults
  useEffect(() => {
    if (entry) {
      setHours(entry.Hours > 0 ? entry.Hours.toString() : '');
      setRateCodeId(entry.RateCodeID?.toString() || '');
      setDescription(entry.Description || '');
      setNonBillable(lumpSum ? true : !!entry.NonBillable);
    } else {
      setHours('');
      // Use the default rate code only if it exists on this job's rate sheet
      const defaultStr = defaultRateCodeId?.toString() || '';
      const defaultValid = defaultStr && filteredRateCodes.some((rc) => rc.RateCodeID?.toString() === defaultStr);
      setRateCodeId(defaultValid ? defaultStr : (filteredRateCodes[0]?.RateCodeID?.toString() || ''));
      setDescription('');
      setNonBillable(lumpSum);
    }
    setDirty(false);
    setSaveError(null);
  }, [entry, defaultRateCodeId, filteredRateCodes, lumpSum]);

  const swipeHandlers = useSwipe({
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp: onPrevFav,
    onSwipeDown: onNextFav,
  });

  // Core save function – returns true on success or no-op, false on error
  const doSave = useCallback(async () => {
    if (savingRef.current || isSubmitted) return true;
    if (!rateCodeId) return true;

    const h = hours ? parseFloat(hours) : 0;
    if (isNaN(h) || h < 0 || h > 24 || (h * 4) % 1 !== 0) return false;
    if (h > maxHours) {
      setSaveError(`No more than ${maxHours} hours can be charged to a holiday.`);
      return false;
    }

    // Don't create a new entry with 0 hours and no description
    if (!entry && h === 0 && !description) return true;

    // Nothing changed
    if (!dirty) return true;

    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({
        hours: h,
        rateCodeId,
        rateSheetCode: favorite?.RateSheetCode || '',
        description,
        nonBillable: lumpSum ? true : nonBillable,
      });
      setDirty(false);
      return true;
    } catch (err) {
      setSaveError(err.message || 'Failed to save');
      return false;
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  }, [hours, rateCodeId, description, nonBillable, entry, dirty, isSubmitted, onSave, favorite, lumpSum, maxHours]);

  // Expose save via ref for parent to call before navigation
  useImperativeHandle(ref, () => ({
    save: () => dirty ? doSave() : Promise.resolve(true),
  }), [dirty, doSave]);

  // Auto-save on blur if dirty and has meaningful data
  const handleBlur = useCallback(() => {
    if (dirty && !isSubmitted && rateCodeId) {
      doSave();
    }
  }, [dirty, isSubmitted, rateCodeId, doSave]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!hours) {
      setSaveError('Hours is required');
      return;
    }
    if (!rateCodeId) {
      setSaveError('Rate code is required');
      return;
    }
    const h = parseFloat(hours);
    if (isNaN(h) || (h * 4) % 1 !== 0) {
      setSaveError('Hours must be in 0.25 increments');
      return;
    }
    await doSave();
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this time entry?')) return;
    setSaving(true);
    try {
      await onDelete();
    } finally {
      setSaving(false);
    }
  };

  if (!favorite) return null;

  const hasRealEntry = entry && parseFloat(entry.Hours) > 0;

  return (
    <div className="entry-swipe-container" {...swipeHandlers}>
      <div className="entry-card">
        {/* Job info header with integrated nav arrows */}
        <div className="entry-card-header">
          <div className="entry-card-info">
            <div className="entry-card-customer">{favorite.CustomerName}</div>
            <div className="entry-card-job">{favorite.JobID} - {favorite.JobName}</div>
            <div className="entry-card-task">{favorite.JobTaskID} – {favorite.TaskCode} – {favorite.TaskName}</div>
          </div>
          <span className="entry-nav-count">{favIndex + 1}/{favCount}</span>
        </div>
        <div className="entry-nav-row">
          <button
            className="entry-nav-btn"
            onClick={onPrevFav}
            disabled={favIndex === 0}
            aria-label="Previous job"
            type="button"
          >&#9650; Job Up{favIndex > 0 ? ` (${favorites[favIndex - 1]?.JobID} / ${favorites[favIndex - 1]?.JobTaskID})` : ''}</button>
          <button
            className="entry-nav-btn"
            onClick={onNextFav}
            aria-label="Next job"
            type="button"
          >&#9660; Job Down{favIndex < favCount - 1 ? ` (${favorites[favIndex + 1]?.JobID} / ${favorites[favIndex + 1]?.JobTaskID})` : ''}</button>
        </div>

        <form onSubmit={handleFormSubmit}>
          {/* Hours and Rate Code on one row */}
          <div className="entry-form-row">
            <div className="form-group" style={{ flex: '0 0 90px' }}>
              <label>Hours</label>
              <input
                type="number"
                className="form-control"
                value={hours}
                onChange={(e) => { setHours(e.target.value); setDirty(true); }}
                onBlur={handleBlur}
                step="0.25"
                min="0"
                max={maxHours}
                placeholder={holidayNA ? 'N/A' : '0.00'}
                disabled={isSubmitted || (holidayNA && !(parseFloat(entry?.Hours) > 0))}
                inputMode="decimal"
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Rate Code</label>
              <select
                className="form-control"
                value={rateCodeId}
                onChange={(e) => { setRateCodeId(e.target.value); setDirty(true); }}
                onBlur={handleBlur}
                disabled={isSubmitted}
              >
                <option value="">Select rate code...</option>
                {filteredRateCodes.map((rc) => (
                  <option key={rc.RateCodeID} value={rc.RateCodeID}>
                    {rc.RateCodeName} (${rc.Rate}/hr)
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              className="form-control"
              value={description}
              maxLength={250}
              onChange={(e) => { setDescription(e.target.value); setDirty(true); }}
              onBlur={handleBlur}
              placeholder="Describe work performed..."
              disabled={isSubmitted}
              rows={2}
            />
          </div>

          {/* Non-billable + Save/Delete on one row */}
          <div className="entry-footer-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="checkbox"
                id="nonBillable"
                checked={lumpSum ? true : nonBillable}
                onChange={(e) => { setNonBillable(e.target.checked); setDirty(true); }}
                onBlur={handleBlur}
                disabled={isSubmitted || lumpSum}
                style={{ width: '16px', height: '16px', cursor: lumpSum ? 'not-allowed' : 'pointer' }}
              />
              <label htmlFor="nonBillable" style={{
                margin: 0, fontSize: '13px',
                cursor: lumpSum ? 'not-allowed' : 'pointer',
                opacity: lumpSum ? 0.6 : 1,
              }}>
                Non-Billable{lumpSum ? ' (Lump Sum)' : ''}
              </label>
            </div>
            {!isSubmitted && (
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {dirty && !saving && (
                  <span style={{ fontSize: '11px', color: 'var(--warning)' }}>unsaved</span>
                )}
                {showSaveNextDay && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={onSaveNextDay}
                    disabled={saving}
                    title="Save and go to the next day"
                  >
                    Save &#8250; Next Day
                  </button>
                )}
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={saving || !rateCodeId}
                >
                  {saving ? 'Saving...' : hasRealEntry ? 'Update' : 'Save'}
                </button>
                {hasRealEntry && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={handleDelete}
                    disabled={saving}
                  >
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>

          {saveError && <Banner kind="err">{saveError}</Banner>}
        </form>
      </div>
    </div>
  );
});

export default EntryCard;
